/* eslint-disable import/prefer-default-export */

import { FEATURE_KEYS } from '../constants';

const createGetLabels = (t) => {
  const PREFIX = 'comparison-table.features.legacy-migration';

  return {
    getTitleLabel: (index) => t(`${PREFIX}.header-${index}.headerText`),
    getLabel: (str) => t(`${PREFIX}.${str}.name`),
    getTooltipLabel: (str) => t(`${PREFIX}.${str}.toolTipText`),
    getTooltipLinkText: (str) => t(`${PREFIX}.${str}.toolTipLinkText`),
    getFullrowLabel: (str) => t(`${PREFIX}.${str}.fullRowText`),

    // 3PL aka V21 plans, we compare legacy plans to them
    getStandardLabel: (str) => t(`${PREFIX}.${str}.standard`),
    getPremiumLabel: (str) => t(`${PREFIX}.${str}.premium`),

    // legacy plans
    getEmailLabel: (str) => t(`${PREFIX}.${str}.email`),
    getEmailPlusLabel: (str) => t(`${PREFIX}.${str}.email-plus`),
    getCoreLabel: (str) => t(`${PREFIX}.${str}.core`),
    getPlusLabel: (str) => t(`${PREFIX}.${str}.plus`),
  };
};

export function getLegacyFeatures(t) {
  const USE_TRANSLATIONS_VALUE = 'USE_TRANSLATIONS_VALUE';
  const USE_DEFAULT_VALUE = 'USE_DEFAULT_VALUE';

  const {
    getTitleLabel,
    getLabel,
    getTooltipLabel,
    getTooltipLinkText,
    getFullrowLabel,
    getStandardLabel,
    getPremiumLabel,
    getEmailLabel,
    getEmailPlusLabel,
    getCoreLabel,
    getPlusLabel,
  } = createGetLabels(t);

  const createTitle = (index) => {
    return {
      titleLabel: getTitleLabel(index),
    };
  };

  const createFeature = (
    { key, defaultValue = false, isFullrowLabel, allValuesShould },
    { email, emailPlus, core, plus, standard, premium } = {}
  ) => {
    const label = getLabel(key);
    const tooltipLabel = getTooltipLabel(key);

    if (isFullrowLabel) {
      return {
        label,
        tooltipLabel,
        fullrowLabel: getFullrowLabel(key),
      };
    }

    const getDetailsValue = (value, cb) => {
      if (allValuesShould === USE_DEFAULT_VALUE) return defaultValue;

      if (value === USE_TRANSLATIONS_VALUE || allValuesShould === USE_TRANSLATIONS_VALUE) return cb(key);

      if (typeof value === 'boolean') return value;

      throw new Error('Unexpected value for legacy features: ', value);
    };

    return {
      label,
      tooltipLabel,
      ...(key === 'google-ads-credit' && {
        tooltipLinkText: getTooltipLinkText(key),
        tooltipLinkUrl: 'https://www.google.com/ads/coupons/terms/',
      }),
      plansDetails: {
        [FEATURE_KEYS.EMAIL]: getDetailsValue(email, getEmailLabel),
        [FEATURE_KEYS.EMAIL_PLUS]: getDetailsValue(emailPlus, getEmailPlusLabel),
        [FEATURE_KEYS.CORE]: getDetailsValue(core, getCoreLabel),
        [FEATURE_KEYS.PLUS]: getDetailsValue(plus, getPlusLabel),
        [FEATURE_KEYS.STANDARD]: getDetailsValue(standard, getStandardLabel),
        [FEATURE_KEYS.PREMIUM]: getDetailsValue(premium, getPremiumLabel),
      },
    };
  };

  return [
    createTitle(0),
    createFeature({ key: 'users', allValuesShould: USE_TRANSLATIONS_VALUE }),
    createFeature({ key: 'storage', allValuesShould: USE_TRANSLATIONS_VALUE }),
    createFeature({ key: 'support', isFullrowLabel: true }),
    createTitle(1),
    createFeature({ key: 'drag-drop-email-templates', defaultValue: true, allValuesShould: USE_DEFAULT_VALUE }),
    createFeature(
      { key: 'ai-content-recommendations' },
      {
        [FEATURE_KEYS.EMAIL]: false,
        [FEATURE_KEYS.EMAIL_PLUS]: false,
        [FEATURE_KEYS.CORE]: false,
        [FEATURE_KEYS.PLUS]: false,
        [FEATURE_KEYS.STANDARD]: true,
        [FEATURE_KEYS.PREMIUM]: true,
      }
    ),
    createFeature(
      { key: 'polls-n-surveys' },
      {
        [FEATURE_KEYS.EMAIL]: USE_TRANSLATIONS_VALUE,
        [FEATURE_KEYS.EMAIL_PLUS]: true,
        [FEATURE_KEYS.CORE]: USE_TRANSLATIONS_VALUE,
        [FEATURE_KEYS.PLUS]: true,
        [FEATURE_KEYS.STANDARD]: true,
        [FEATURE_KEYS.PREMIUM]: true,
      }
    ),
    createFeature({ key: 'scheduled-email-sends', defaultValue: true, allValuesShould: USE_DEFAULT_VALUE }),
    createFeature({ key: 'subject-line-AB-testing', defaultValue: true, allValuesShould: USE_DEFAULT_VALUE }),
    createFeature({ key: 'subject-line-recommendations', defaultValue: true, allValuesShould: USE_DEFAULT_VALUE }),
    createFeature({ key: 'subject-line-personalization', defaultValue: true, allValuesShould: USE_DEFAULT_VALUE }),
    createFeature(
      { key: 'dynamic-content-in-email' },
      {
        [FEATURE_KEYS.EMAIL]: false,
        [FEATURE_KEYS.EMAIL_PLUS]: true,
        [FEATURE_KEYS.CORE]: false,
        [FEATURE_KEYS.PLUS]: true,
        [FEATURE_KEYS.STANDARD]: false,
        [FEATURE_KEYS.PREMIUM]: true,
      }
    ),
    createFeature({ key: 'reach-and-engagement-reporting', defaultValue: true, allValuesShould: USE_DEFAULT_VALUE }),
    createFeature({ key: 'drill-down-reporting', defaultValue: true, allValuesShould: USE_DEFAULT_VALUE }),
    createFeature(
      { key: 'engagement-heatmap-reporting' },
      {
        [FEATURE_KEYS.EMAIL]: true,
        [FEATURE_KEYS.EMAIL_PLUS]: true,
        [FEATURE_KEYS.CORE]: true,
        [FEATURE_KEYS.PLUS]: true,
        [FEATURE_KEYS.STANDARD]: false,
        [FEATURE_KEYS.PREMIUM]: true,
      }
    ),
    createFeature(
      { key: 'email-newsletter-archive' },
      {
        [FEATURE_KEYS.EMAIL]: false,
        [FEATURE_KEYS.EMAIL_PLUS]: true,
        [FEATURE_KEYS.CORE]: false,
        [FEATURE_KEYS.PLUS]: true,
        [FEATURE_KEYS.STANDARD]: true,
        [FEATURE_KEYS.PREMIUM]: true,
      }
    ),
    createFeature({ key: 'inbox-preview', isFullrowLabel: true }),
    createFeature(
      { key: 'product-block' },
      {
        [FEATURE_KEYS.EMAIL]: false,
        [FEATURE_KEYS.EMAIL_PLUS]: false,
        [FEATURE_KEYS.CORE]: false,
        [FEATURE_KEYS.PLUS]: true,
        [FEATURE_KEYS.STANDARD]: true,
        [FEATURE_KEYS.PREMIUM]: true,
      }
    ),
    createTitle(2),
    createFeature({ key: 'automated-welcome-email', defaultValue: true, allValuesShould: USE_DEFAULT_VALUE }),
    createFeature(
      { key: 'resend-email-to-non-openers' },
      {
        [FEATURE_KEYS.EMAIL]: true,
        [FEATURE_KEYS.EMAIL_PLUS]: true,
        [FEATURE_KEYS.CORE]: false,
        [FEATURE_KEYS.PLUS]: true,
        [FEATURE_KEYS.STANDARD]: true,
        [FEATURE_KEYS.PREMIUM]: true,
      }
    ),
    createFeature(
      { key: 'automation-path-templates' },
      {
        [FEATURE_KEYS.EMAIL]: true,
        [FEATURE_KEYS.EMAIL_PLUS]: true,
        [FEATURE_KEYS.CORE]: false,
        [FEATURE_KEYS.PLUS]: true,
        [FEATURE_KEYS.STANDARD]: false,
        [FEATURE_KEYS.PREMIUM]: true,
      }
    ),
    createFeature(
      { key: 'custom-automation-paths' },
      {
        [FEATURE_KEYS.EMAIL]: false,
        [FEATURE_KEYS.EMAIL_PLUS]: false,
        [FEATURE_KEYS.CORE]: false,
        [FEATURE_KEYS.PLUS]: false,
        [FEATURE_KEYS.STANDARD]: false,
        [FEATURE_KEYS.PREMIUM]: true,
      }
    ),
    createTitle(3),
    createFeature({ key: 'contact-management', defaultValue: true, allValuesShould: USE_DEFAULT_VALUE }),
    createFeature({ key: 'lead-generation-landing-pages', defaultValue: true, allValuesShould: USE_DEFAULT_VALUE }),
    createFeature({ key: 'contact-segmentation', defaultValue: true, allValuesShould: USE_DEFAULT_VALUE }),
    createFeature(
      { key: 'custom-segmentation' },
      {
        [FEATURE_KEYS.EMAIL]: false,
        [FEATURE_KEYS.EMAIL_PLUS]: true,
        [FEATURE_KEYS.CORE]: false,
        [FEATURE_KEYS.PLUS]: true,
        [FEATURE_KEYS.STANDARD]: true,
        [FEATURE_KEYS.PREMIUM]: true,
      }
    ),
    createFeature(
      { key: 'contact-activity-segmentation' },
      {
        [FEATURE_KEYS.EMAIL]: false,
        [FEATURE_KEYS.EMAIL_PLUS]: true,
        [FEATURE_KEYS.CORE]: false,
        [FEATURE_KEYS.PLUS]: true,
        [FEATURE_KEYS.STANDARD]: true,
        [FEATURE_KEYS.PREMIUM]: true,
      }
    ),
    createTitle(4),
    createFeature({ key: 'registration-form', defaultValue: true, allValuesShould: USE_DEFAULT_VALUE }),
    createFeature({ key: 'payments', defaultValue: true, allValuesShould: USE_DEFAULT_VALUE }),
    createFeature({ key: 'sell-products-n-services', defaultValue: true, allValuesShould: USE_DEFAULT_VALUE }),
    createTitle(5),
    createFeature({ key: 'sms-campaigns', allValuesShould: USE_TRANSLATIONS_VALUE }),
    createFeature(
      { key: 'text-to-join' },
      {
        [FEATURE_KEYS.EMAIL]: USE_TRANSLATIONS_VALUE,
        [FEATURE_KEYS.EMAIL_PLUS]: USE_TRANSLATIONS_VALUE,
        [FEATURE_KEYS.CORE]: USE_TRANSLATIONS_VALUE,
        [FEATURE_KEYS.PLUS]: USE_TRANSLATIONS_VALUE,
        [FEATURE_KEYS.STANDARD]: USE_TRANSLATIONS_VALUE,
        [FEATURE_KEYS.PREMIUM]: true,
      }
    ),
    createFeature(
      { key: 'sms-reporting' },
      {
        [FEATURE_KEYS.EMAIL]: USE_TRANSLATIONS_VALUE,
        [FEATURE_KEYS.EMAIL_PLUS]: USE_TRANSLATIONS_VALUE,
        [FEATURE_KEYS.CORE]: USE_TRANSLATIONS_VALUE,
        [FEATURE_KEYS.PLUS]: USE_TRANSLATIONS_VALUE,
        [FEATURE_KEYS.STANDARD]: USE_TRANSLATIONS_VALUE,
        [FEATURE_KEYS.PREMIUM]: true,
      }
    ),
    createFeature(
      { key: 'ai-content-recommendations-2' },
      {
        [FEATURE_KEYS.EMAIL]: false,
        [FEATURE_KEYS.EMAIL_PLUS]: false,
        [FEATURE_KEYS.CORE]: false,
        [FEATURE_KEYS.PLUS]: false,
        [FEATURE_KEYS.STANDARD]: true,
        [FEATURE_KEYS.PREMIUM]: true,
      }
    ),
    createTitle(6),
    createFeature({
      key: 'post-on-facebook-instagram-linkedin',
      defaultValue: true,
      allValuesShould: USE_DEFAULT_VALUE,
    }),
    createFeature({ key: 'social-reporting', defaultValue: true, allValuesShould: USE_DEFAULT_VALUE }),
    createFeature({ key: 'schedule-posts-in-advance', defaultValue: true, allValuesShould: USE_DEFAULT_VALUE }),
    createFeature(
      { key: 'ai-content-recommendations-3' },
      {
        [FEATURE_KEYS.EMAIL]: false,
        [FEATURE_KEYS.EMAIL_PLUS]: false,
        [FEATURE_KEYS.CORE]: false,
        [FEATURE_KEYS.PLUS]: false,
        [FEATURE_KEYS.STANDARD]: true,
        [FEATURE_KEYS.PREMIUM]: true,
      }
    ),
    createTitle(7),
    createFeature({ key: 'social-media-ads-manager', defaultValue: true, allValuesShould: USE_DEFAULT_VALUE }),
    createFeature(
      { key: 'facebook-lookalike-ad-targeting' },
      {
        [FEATURE_KEYS.EMAIL]: false,
        [FEATURE_KEYS.EMAIL_PLUS]: false,
        [FEATURE_KEYS.CORE]: false,
        [FEATURE_KEYS.PLUS]: true,
        [FEATURE_KEYS.STANDARD]: false,
        [FEATURE_KEYS.PREMIUM]: true,
      }
    ),
    createFeature(
      { key: 'google-ads-manager' },
      {
        [FEATURE_KEYS.EMAIL]: true,
        [FEATURE_KEYS.EMAIL_PLUS]: true,
        [FEATURE_KEYS.CORE]: false,
        [FEATURE_KEYS.PLUS]: true,
        [FEATURE_KEYS.STANDARD]: false,
        [FEATURE_KEYS.PREMIUM]: true,
      }
    ),
    createFeature(
      { key: 'google-ads-credit' },
      {
        [FEATURE_KEYS.EMAIL]: false,
        [FEATURE_KEYS.EMAIL_PLUS]: false,
        [FEATURE_KEYS.CORE]: false,
        [FEATURE_KEYS.PLUS]: false,
        [FEATURE_KEYS.STANDARD]: false,
        [FEATURE_KEYS.PREMIUM]: true,
      }
    ),
    createTitle(8),
    createFeature({ key: 'marketing-campaign-calendar', defaultValue: true, allValuesShould: USE_DEFAULT_VALUE }),
    createFeature({ key: '300-plus-integrations', defaultValue: true, allValuesShould: USE_DEFAULT_VALUE }),
    createFeature(
      { key: 'revenue-reporting' },
      {
        [FEATURE_KEYS.EMAIL]: true,
        [FEATURE_KEYS.EMAIL_PLUS]: true,
        [FEATURE_KEYS.CORE]: false,
        [FEATURE_KEYS.PLUS]: true,
        [FEATURE_KEYS.STANDARD]: false,
        [FEATURE_KEYS.PREMIUM]: true,
      }
    ),
    createFeature({ key: 'seo-recommendations', isFullrowLabel: true }),
  ];
}
